/*!
 * shade-pdp.js — Family-B shade configurator extras
 *
 * Loaded after the inline IIFE in sections/shade-pdp.liquid. Reads the same
 * #shade-pdp-config JSON the inline script reads, plus the helpers exposed on
 * window.__psCore. Adds three features:
 *
 *   1. Multi-shade project builder
 *      - psAddAnotherShade()   pushes the current configuration onto a running
 *        list and resets the form so the customer can configure the next window
 *      - psRemoveShade(idx)    removes a shade from the running list
 *      - psAddAllToCart()      POSTs the whole list to /cart/add.js in ONE call
 *        (each shade + its free brackets + any paid extras)
 *
 *   2. Sticky bottom price/project bar
 *      - Toggles visibility when the in-page Add-to-Cart row scrolls offscreen
 *      - Shows current size-tier price and shade count
 *
 *   3. Brackets sub-cart
 *      - 2 free brackets auto-added per shade (variant ID from section settings)
 *      - Optional paid extras (variant ID + price from section settings)
 *      - Wraps psAddToCart so both single ATC and Add All to Cart pull the same
 *        bracket items through buildCartItems()
 *
 * Family A (shade-config.js, shade-config-steps*.liquid) is untouched. Bracket
 * variant IDs and pricing live in section/template settings, never hardcoded.
 */
(function () {
  // Wait for __psCore (the inline script registers it at end of IIFE).
  function whenReady(cb) {
    if (window.__psCore) return cb();
    var tries = 0;
    var t = setInterval(function () {
      if (window.__psCore || ++tries > 50) {
        clearInterval(t);
        if (window.__psCore) cb();
      }
    }, 20);
  }

  whenReady(function () {
    var CORE  = window.__psCore;
    var CFG   = CORE.config;
    var BRACK = (CFG && CFG.brackets) || { enabled: false };
    var DAY_OPTS = (CFG && CFG.dayShadeOptions) || [];

    // ── State ────────────────────────────────────────────────────────────
    var _selectedDayKey  = DAY_OPTS.length ? DAY_OPTS[0].dayKey  : null;
    var _selectedDayName = DAY_OPTS.length ? DAY_OPTS[0].dayName : null;
    window._psShades = window._psShades || [];
    var _editingShadeId = null;

    // ── localStorage persistence ─────────────────────────────────────────
    // One order follows the customer across every shade PDP. Legacy per-product
    // lists are imported once so an older saved project is not stranded.
    var STORAGE_KEY = 'ervp:shade-order';
    var LEGACY_STORAGE_KEY = 'ps-shades:' + (CFG.sourceProductHandle || 'default');
    var PENDING_EDIT_KEY = 'ervp:shade-order:pending-edit';
    var STORAGE_VERSION = 2;
    // Entries older than this are treated as expired and silently dropped.
    var STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

    function safeLocalStorage() {
      try { return window.localStorage; } catch (e) { return null; }
    }

    function loadShadesFromStorage() {
      var ls = safeLocalStorage();
      if (!ls) return { shades: [], notices: [] };
      var raw;
      try { raw = ls.getItem(STORAGE_KEY); } catch (e) { return { shades: [], notices: [] }; }
      if (!raw) {
        try { raw = ls.getItem(LEGACY_STORAGE_KEY); } catch (e) { raw = null; }
      }
      if (!raw) return { shades: [], notices: [] };
      var parsed;
      try { parsed = JSON.parse(raw); } catch (e) { return { shades: [], notices: [] }; }
      if (!parsed || !Array.isArray(parsed.shades)) {
        return { shades: [], notices: [] };
      }
      var now = Date.now();
      var notices = [];
      var dropped = 0, repriced = 0, missingRail = 0;
      var validated = parsed.shades.filter(function (s) {
        if (!s || !s.variantId) return false;
        if (s.savedAt && (now - s.savedAt > STORAGE_TTL_MS)) { dropped++; return false; }
        s.id = s.id || ('shade-' + now + '-' + Math.random().toString(36).slice(2, 8));
        s.sourceProductHandle = s.sourceProductHandle || CFG.sourceProductHandle || null;
        s.sourceProductTitle = s.sourceProductTitle || CFG.sourceProductTitle || null;
        s.sourceProductUrl = s.sourceProductUrl || (s.sourceProductHandle ? ('/products/' + s.sourceProductHandle) : (CFG.productUrl || window.location.pathname));
        s.editorState = s.editorState || editorStateFromStoredShade(s);
        s.brackets = s.brackets || copyBracketConfig();
        var isCurrentProduct = !s.sourceProductHandle || s.sourceProductHandle === CFG.sourceProductHandle;
        var tier = isCurrentProduct ? findTier(s.variantId) : null;
        if (isCurrentProduct && CFG.variantMode === 'by_size_tier' && !tier) {
          dropped++;
          return false;
        }
        if (isCurrentProduct && CFG.showRailColor) {
          s.properties = s.properties || {};
          var requiredRail = getValidRailName(s.properties['Top & Pull Rail Color'] || s.properties['Rail Color']);
          if (!requiredRail) {
            missingRail++;
            return false;
          }
          s.properties['Top & Pull Rail Color'] = requiredRail;
          delete s.properties['Rail Color'];
        }
        if (tier && Number(tier.price) !== Number(s.price)) {
          s.priceWas = Number(s.price);
          s.price = Number(tier.price);
          s.stale = true;
          repriced++;
        }
        return true;
      });
      if (dropped > 0) notices.push(dropped + ' previously-saved item' + (dropped > 1 ? 's were' : ' was') + ' removed (no longer available).');
      if (repriced > 0) notices.push(repriced + ' item' + (repriced > 1 ? 's had' : ' had') + ' their price updated to current.');
      if (missingRail > 0) notices.push(missingRail + ' previously-saved shade' + (missingRail > 1 ? 's need' : ' needs') + ' you to choose a top & pull rail color again.');
      try {
        if (raw && !ls.getItem(STORAGE_KEY) && validated.length) {
          ls.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, savedAt: now, shades: validated }));
          ls.removeItem(LEGACY_STORAGE_KEY);
          notices.push('Your previous shade list was restored.');
        }
      } catch (e) { /* storage may be unavailable */ }
      return { shades: validated, notices: notices };
    }

    function saveShadesToStorage() {
      var ls = safeLocalStorage();
      if (!ls) return;
      try {
        if (!window._psShades.length) {
          ls.removeItem(STORAGE_KEY);
        } else {
          ls.setItem(STORAGE_KEY, JSON.stringify({
            version: STORAGE_VERSION,
            savedAt: Date.now(),
            shades: window._psShades
          }));
        }
      } catch (e) { /* quota / private mode — ignore silently */ }
    }

    function findTier(variantId) {
      if (!CFG.sizeTiers) return null;
      for (var i = 0; i < CFG.sizeTiers.length; i++) {
        if (String(CFG.sizeTiers[i].varId) === String(variantId)) return CFG.sizeTiers[i];
      }
      return null;
    }

    function getValidRailName(railValue) {
      if (CORE.getValidRailName) return CORE.getValidRailName(railValue);
      if (!railValue || !CFG.railOptions) return null;
      var candidate = String(railValue).trim();
      for (var i = 0; i < CFG.railOptions.length; i++) {
        var option = CFG.railOptions[i] || {};
        var optionKey = String(option.railKey || '').trim();
        var optionName = String(option.railName || '').trim();
        if (optionName && (candidate === optionKey || candidate === optionName)) return optionName;
      }
      return null;
    }

    function copyBracketConfig() {
      return {
        enabled: !!BRACK.enabled,
        freeVariantId: BRACK.freeVariantId || null,
        paidVariantId: BRACK.paidVariantId || null,
        paidPrice: Number(BRACK.paidPrice || 0),
        freePerShade: Number(BRACK.freePerShade || 2)
      };
    }

    function numberFromDimension(value) {
      if (value == null) return 0;
      var text = String(value).replace(/[^\d.]/g, '');
      return parseFloat(text) || 0;
    }

    function editorStateFromStoredShade(shade) {
      var props = (shade && shade.properties) || {};
      var width = shade && shade.width != null ? Number(shade.width) : numberFromDimension(props.Width || shade.widthStr);
      var height = shade && shade.height != null ? Number(shade.height) : numberFromDimension(props.Height || shade.heightStr);
      return {
        width: width,
        height: height,
        mount: props.Mount || (shade && shade.mountType) || 'Inside',
        lift: props.Lift || (shade && shade.liftType) || 'Corded',
        fabricKey: (shade && (shade.fabricKey || shade.fabric || shade.nightFabric)) || null,
        colorName: props['Night Shade Color'] || props['Fabric Color'] || props.Color || (shade && shade.color) || null,
        dayKey: (shade && shade.dayKey) || null,
        dayName: props['Day Shade Color'] || (shade && shade.dayFabric) || null,
        railKey: (shade && shade.railKey) || null,
        railName: props['Top & Pull Rail Color'] || (shade && shade.railColor) || null,
        quantity: Number((shade && shade.quantity) || 1),
        windowName: props['Window Name'] || '',
        extraBrackets: Number((shade && shade.extraBrackets) || 0)
      };
    }

    function currentEditorState(payload) {
      var state = CORE.getState ? CORE.getState() : {};
      var dims = CORE.getDims ? CORE.getDims() : { w: 0, h: 0 };
      var props = payload.properties || {};
      return {
        width: dims.w,
        height: dims.h,
        mount: state.mount || props.Mount || 'Inside',
        lift: state.lift || props.Lift || 'Corded',
        fabricKey: state.selectedFabric || null,
        colorName: state.selectedColorName || props['Night Shade Color'] || props['Fabric Color'] || props.Color || null,
        dayKey: _selectedDayKey || null,
        dayName: _selectedDayName || props['Day Shade Color'] || null,
        railKey: state.selectedRail || null,
        railName: state.selectedRailName || props['Top & Pull Rail Color'] || null,
        quantity: Number(payload.quantity || 1),
        windowName: props['Window Name'] || '',
        extraBrackets: getExtraBrackets()
      };
    }

    function showListNotice(text) {
      var el = $('ps-shade-list-notice');
      if (!el) return;
      if (text) {
        el.textContent = text;
        el.style.display = 'block';
      } else {
        el.style.display = 'none';
        el.textContent = '';
      }
    }

    // ── DOM helpers ──────────────────────────────────────────────────────
    function $(id) { return document.getElementById(id); }
    function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

    // ── Day shade swatch selection ──────────────────────────────────────
    // Preserve the section's core handler so lifestyle imagery, shareable
    // color URLs, summaries, and this multi-shade module all stay in sync.
    var coreSelectDayShade = window.psSelectDayShade;
    window.psSelectDayShade = function (el, skipUrlUpdate) {
      if (typeof coreSelectDayShade === 'function') coreSelectDayShade(el, skipUrlUpdate);
      $$('.ps-day-card').forEach(function (c) { c.classList.remove('active'); });
      el.classList.add('active');
      _selectedDayKey  = el.dataset.dayKey  || null;
      _selectedDayName = el.dataset.dayName || null;
      CORE.setText('ps-day-shade-summary', _selectedDayName || 'Select sheer');
    };

    // ── Brackets quantity controls ──────────────────────────────────────
    function getExtraBrackets() {
      var el = $('ps-brackets-extra');
      return el ? Math.max(0, parseInt(el.value) || 0) : 0;
    }
    function setExtraBrackets(n) {
      var el = $('ps-brackets-extra');
      if (!el) return;
      n = Math.max(0, Math.min(20, n));
      el.value = n;
      var costEl = $('ps-bracket-extra-cost');
      if (costEl) {
        costEl.textContent = n > 0
          ? ('+ $' + (n * Number(BRACK.paidPrice || 0)).toFixed(2))
          : '';
      }
      var sumEl = $('ps-bracket-summary');
      if (sumEl) {
        sumEl.textContent = n > 0
          ? ('2 free + ' + n + ' paid')
          : '2 included free';
      }
    }
    window.psAdjBrackets = function (delta) {
      setExtraBrackets(getExtraBrackets() + delta);
    };

    // ── Build cart line items (shade + brackets) ────────────────────────
    // Returns an array suitable for POST /cart/add.js {"items":[…]}.
    function buildCartItems(payload, shadeQty) {
      var items = [{
        id:         payload.variantId,
        quantity:   shadeQty,
        properties: payload.properties
      }];
      if (BRACK.enabled && BRACK.freeVariantId) {
        var perShade = Number(BRACK.freePerShade || 2);
        items.push({
          id: BRACK.freeVariantId,
          quantity: shadeQty * perShade,
          properties: { '_included_with_shade': perShade + ' per shade' }
        });
      }
      var extra = getExtraBrackets();
      if (BRACK.enabled && BRACK.paidVariantId && extra > 0) {
        items.push({
          id: BRACK.paidVariantId,
          quantity: extra,
          properties: { '_extra_brackets': 'Yes' }
        });
      }
      return items;
    }

    // ── Normalize buildProjectPayload day-shade property ────────────────
    // Keep one customer-facing property name across inline and legacy builders.
    // The wrapper also refreshes it from this script's selected day-shade state.
    var _origBuildPayload = window.buildProjectPayload;
    window.buildProjectPayload = function () {
      var p = _origBuildPayload();
      if (!p || p.error) return p;
      if (_selectedDayKey) {
        p.properties = p.properties || {};
        p.properties['Day Shade Color'] = _selectedDayName || _selectedDayKey;
        if (p.projectShade) {
          p.projectShade.dayFabric = _selectedDayKey;
        }
      }
      return p;
    };

    // ── Cart transport ──────────────────────────────────────────────────
    // Never repeat a POST after an uncertain response: Shopify may have added
    // the items already. Compare quantities with a pre-request cart instead.
    var _cartRequestPending = false;
    function cartRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }
    function readCart() {
      return fetch(cartRoot() + 'cart.js', { credentials: 'same-origin', cache: 'no-store' })
        .then(function (response) {
          if (!response.ok) throw new Error('Cart unavailable');
          return response.json();
        }).then(function (cart) {
          if (!cart || !Array.isArray(cart.items)) throw new Error('Cart unavailable');
          return cart;
        });
    }
    function itemKey(item) {
      var props = item.properties || {};
      return JSON.stringify([String(item.variant_id || item.id), Object.keys(props).sort().filter(function (key) {
        return props[key] != null && String(props[key]) !== '';
      }).map(function (key) { return [key, String(props[key])]; })]);
    }
    function quantities(items) {
      var counts = Object.create(null);
      items.forEach(function (item) {
        var key = itemKey(item);
        counts[key] = (counts[key] || 0) + Number(item.quantity || 0);
      });
      return counts;
    }
    function cartContainsAddition(before, after, items) {
      var oldCounts = quantities(before.items), newCounts = quantities(after.items), needed = quantities(items);
      return Object.keys(needed).every(function (key) {
        return (newCounts[key] || 0) >= (oldCounts[key] || 0) + needed[key];
      });
    }
    function addCartItems(items) {
      var before = null;
      return readCart().then(function (cart) { before = cart; }, function () {
        // A failed preflight read must not prevent an otherwise valid add.
      }).then(function () {
        return fetch(cartRoot() + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        }).catch(function () {
          throw new Error('The connection was interrupted while adding your shade.');
        });
      }).then(function (response) {
        return response.json().catch(function () {
          throw new Error('The store returned an unreadable cart response' + (response.status ? ' (HTTP ' + response.status + ')' : '') + '.');
        }).then(function (data) {
          if (!response.ok || !(data && (data.items || data.id))) {
            var detail = data && (data.description || data.message);
            throw new Error(typeof detail === 'string' ? detail : 'The store could not add your shade.');
          }
          return data;
        });
      }).catch(function (error) {
        if (!before) throw new Error(error.message + ' Please check your cart before trying again.');
        return readCart().then(function (after) {
          if (cartContainsAddition(before, after, items)) return { items: items, recovered: true };
          throw new Error(error.message + ' Please check your cart before trying again. Your selections are still here.');
        }, function () {
          throw new Error(error.message + ' We could not confirm your cart. Please check it before trying again.');
        });
      });
    }
    function showCartError(error) {
      CORE.showMsg('error', error.message || 'Could not confirm your cart. Please check it before trying again.');
      var message = $('ps-msg');
      if (message) {
        var link = document.createElement('a');
        link.href = cartRoot() + 'cart';
        link.textContent = ' View cart';
        link.style.textDecoration = 'underline';
        message.appendChild(link);
      }
    }

    // ── Replace psAddToCart so brackets ride along ──────────────────────
    window.psAddToCart = function () {
      if (_cartRequestPending) return;
      CORE.clearMsg();
      var payload = window.buildProjectPayload();
      if (!payload || payload.error) {
        CORE.showMsg('error', (payload && payload.error) || 'Could not build cart payload.');
        return;
      }
      var atcBtn = $('ps-atc-btn');
      var stickyBtn = $('ps-sticky-bar-atc');
      _cartRequestPending = true;
      [atcBtn, stickyBtn].forEach(function (b) { if (b) { b.disabled = true; if (b === atcBtn) b.textContent = 'Adding…'; } });

      return addCartItems(buildCartItems(payload, payload.quantity)).then(function () {
        CORE.showMsg('success', '✓ Added to cart! ' + payload.summaryText);
        window.location.href = cartRoot() + 'cart';
      }).catch(function (error) {
        _cartRequestPending = false;
        [atcBtn, stickyBtn].forEach(function (b) { if (b) { b.disabled = false; if (b === atcBtn) b.textContent = 'Add to Cart'; } });
        showCartError(error);
      });
    };

    // ── Multi-shade project builder ─────────────────────────────────────
    function priceForShade(payload) {
      // For size-tier mode, look up the tier price from the resolved variant.
      if (CFG.variantMode !== 'by_size_tier') return null;
      var match = null;
      for (var i = 0; i < CFG.sizeTiers.length; i++) {
        var t = CFG.sizeTiers[i];
        if (String(t.varId) === String(payload.variantId)) { match = t; break; }
      }
      return match ? Number(match.price) : null;
    }

    function renderShadeList() {
      var wrap = $('ps-shade-list-wrap');
      var list = $('ps-shade-list');
      var cnt  = $('ps-shade-count');
      var tot  = $('ps-shade-list-total');
      if (!wrap || !list) return;
      if (!window._psShades.length) {
        wrap.style.display = 'none';
        showListNotice(null);
        updateStickyBar();
        return;
      }
      wrap.style.display = 'block';
      list.innerHTML = '';
      var total = 0;
      var totalQuantity = 0;
      window._psShades.forEach(function (s, i) {
        var lineTotal = (Number(s.price) || 0) * (Number(s.quantity) || 1);
        total += lineTotal;
        totalQuantity += Number(s.quantity) || 1;
        var row = document.createElement('div');
        row.className = 'ps-shade-row';
        var staleNote = '';
        if (s.stale && s.priceWas != null) {
          staleNote = ' <span style="font-size:11px; color:#92400e; font-weight:600; margin-left:6px;">price updated (was $' + Number(s.priceWas).toFixed(2) + ')</span>';
        }
        row.innerHTML =
          '<div class="ps-shade-row-info">' +
            '<div class="ps-shade-row-title"></div>' +
            '<div class="ps-shade-row-meta"></div>' +
          '</div>' +
          '<div class="ps-shade-row-price">' + (s.price ? ('$' + lineTotal.toFixed(2)) : '') + staleNote + '</div>' +
          '<div class="ps-shade-row-actions">' +
            '<button class="ps-shade-row-action ps-shade-row-edit" type="button">Edit</button>' +
            '<button class="ps-shade-row-action ps-shade-row-duplicate" type="button">Duplicate</button>' +
            '<button class="ps-shade-row-action ps-shade-row-remove" type="button">Remove</button>' +
          '</div>';
        var windowName = (s.properties && s.properties['Window Name']) || '';
        row.querySelector('.ps-shade-row-title').textContent = windowName || s.label || 'Custom Shade';
        var metaBits = [];
        if (windowName && s.label) metaBits.push(s.label);
        if (s.sourceProductTitle && s.sourceProductHandle !== CFG.sourceProductHandle) metaBits.push(s.sourceProductTitle);
        if (s.summaryText) metaBits.push(s.summaryText);
        if ((Number(s.quantity) || 1) > 1) metaBits.push('Qty ' + s.quantity);
        row.querySelector('.ps-shade-row-meta').textContent = metaBits.join(' · ');
        row.querySelector('.ps-shade-row-edit').addEventListener('click', function () {
          window.psEditShade(s.id);
        });
        row.querySelector('.ps-shade-row-duplicate').addEventListener('click', function () {
          window.psDuplicateShade(s.id);
        });
        row.querySelector('.ps-shade-row-remove').addEventListener('click', function () {
          window.psRemoveShade(i);
        });
        list.appendChild(row);
      });
      if (cnt) cnt.textContent = String(window._psShades.length);
      if (tot) tot.textContent = '$' + total.toFixed(2);
      var addAllBtn = $('ps-add-all-btn');
      if (addAllBtn) addAllBtn.textContent = 'Add All ' + totalQuantity + ' Shade' + (totalQuantity === 1 ? '' : 's') + ' to Cart';
      updateStickyBar();
    }

    function resetForm() {
      var ids = ['ps-width-whole', 'ps-width-frac', 'ps-height-whole', 'ps-height-frac'];
      ids.forEach(function (id) {
        var el = $(id);
        if (el) el.value = (id === 'ps-width-frac' || id === 'ps-height-frac') ? '0' : '';
      });
      var qty = $('ps-qty'); if (qty) qty.value = 1;
      var wn  = $('ps-window-name'); if (wn) wn.value = '';
      if (window.psResetRailSelection) window.psResetRailSelection();
      setExtraBrackets(0);
      if (CORE.updateSizeSummary) CORE.updateSizeSummary();
      if (CORE.updatePrice)        CORE.updatePrice();
    }

    function setDimension(prefix, value) {
      var numeric = Number(value) || 0;
      var whole = Math.floor(numeric);
      var fraction = Math.round((numeric - whole) * 100) / 100;
      var wholeEl = $('ps-' + prefix + '-whole');
      var fracEl = $('ps-' + prefix + '-frac');
      if (wholeEl) wholeEl.value = whole ? String(whole) : '';
      if (fracEl) fracEl.value = String(fraction);
    }

    function setEditingUi(shade) {
      var btn = $('ps-save-btn');
      var cancel = $('ps-cancel-edit-btn');
      var windowName = shade && shade.properties && shade.properties['Window Name'];
      if (btn) btn.textContent = shade ? ('Update ' + (windowName || 'Shade')) : 'Save Shade & Build Another';
      if (cancel) cancel.style.display = shade ? 'block' : 'none';
    }

    function restoreShadeIntoForm(shade) {
      if (!shade) return;
      var state = shade.editorState || editorStateFromStoredShade(shade);
      setDimension('width', state.width);
      setDimension('height', state.height);

      var color = Array.prototype.find.call($$('#ps-colors .ps-color-card'), function (card) {
        return (state.fabricKey && card.dataset.fabric === state.fabricKey) ||
          (state.colorName && card.dataset.color === state.colorName);
      });
      if (color && window.psSelectColor) window.psSelectColor(color, true);

      var day = Array.prototype.find.call($$('#ps-day-shades .ps-day-card'), function (card) {
        return (state.dayKey && card.dataset.dayKey === state.dayKey) ||
          (state.dayName && card.dataset.dayName === state.dayName);
      });
      if (day && window.psSelectDayShade) window.psSelectDayShade(day, true);

      var rail = Array.prototype.find.call($$('.ps-rail-card'), function (card) {
        return (state.railKey && card.dataset.rail === state.railKey) ||
          (state.railName && card.dataset.railName === state.railName);
      });
      if (rail && window.psSelectRail) window.psSelectRail(rail);

      var mountButton = $('ps-mount-' + String(state.mount || 'Inside').toLowerCase());
      if (mountButton && window.psSelectMount) window.psSelectMount(state.mount || 'Inside', mountButton);
      var liftButton = $('ps-lift-' + String(state.lift || 'Corded').toLowerCase());
      if (liftButton && window.psSelectLift) window.psSelectLift(state.lift || 'Corded', liftButton);

      var qty = $('ps-qty'); if (qty) qty.value = Math.max(1, Math.min(20, Number(state.quantity) || 1));
      var wn = $('ps-window-name'); if (wn) {
        wn.value = state.windowName || '';
        var count = $('ps-window-name-count'); if (count) count.textContent = wn.value.length + '/40';
      }
      setExtraBrackets(state.extraBrackets || 0);
      if (CORE.updateSizeSummary) CORE.updateSizeSummary();
      if (CORE.updatePrice) CORE.updatePrice();
      setEditingUi(shade);
      var top = $('ps-pdp');
      if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
      CORE.showMsg('success', 'Editing ' + (state.windowName || 'saved shade') + '. Update it when you are finished.');
    }

    window.psAddAnotherShade = function () {
      CORE.clearMsg();
      var payload = window.buildProjectPayload();
      if (!payload || payload.error) {
        CORE.showMsg('error', (payload && payload.error) || 'Could not build shade.');
        return;
      }
      var price = priceForShade(payload);
      var extra = getExtraBrackets();
      var projectShade = payload.projectShade || {};
      var entry = {
        id:            _editingShadeId || projectShade.id || ('shade-' + Date.now()),
        variantId:   payload.variantId,
        quantity:    payload.quantity,
        properties:  payload.properties,
        summaryText: payload.summaryText,
        widthStr:    payload.widthStr,
        heightStr:   payload.heightStr,
        price:       price,
        extraBrackets: extra,
        editorState: currentEditorState(payload),
        sourceProductHandle: CFG.sourceProductHandle || null,
        sourceProductTitle: CFG.sourceProductTitle || null,
        sourceProductUrl: CFG.productUrl || window.location.pathname,
        brackets: copyBracketConfig(),
        label: (CFG.shadeType ? (CFG.shadeType + ' ') : '') +
               ((payload.properties && (payload.properties['Night Shade Color'] || payload.properties['Fabric Color'] || payload.properties['Color'])) || '') +
               ' Shade'
      };
      // Stamp the save time so localStorage TTL can expire old entries
      entry.savedAt = Date.now();
      var editingIndex = window._psShades.findIndex(function (shade) { return shade.id === _editingShadeId; });
      if (editingIndex > -1) window._psShades.splice(editingIndex, 1, entry);
      else window._psShades.push(entry);
      var wasEditing = editingIndex > -1;
      _editingShadeId = null;
      saveShadesToStorage();
      // Adding a fresh shade implicitly clears any "restored from previous session" notice
      showListNotice(null);
      renderShadeList();
      refreshDisplayedPrices();

      var btn = $('ps-save-btn');
      if (btn) {
        var orig = btn.textContent;
        btn.disabled = true; btn.textContent = wasEditing ? 'Updated ✓' : 'Saved ✓';
        setTimeout(function () { btn.disabled = false; setEditingUi(null); }, 900);
      }
      resetForm();
      // Scroll back to the top of the configurator so the customer can start fresh
      var top = document.getElementById('ps-pdp');
      if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    window.psRemoveShade = function (idx) {
      if (window._psShades[idx] && window._psShades[idx].id === _editingShadeId) window.psCancelShadeEdit();
      window._psShades.splice(idx, 1);
      saveShadesToStorage();
      renderShadeList();
    };

    window.psDuplicateShade = function (id) {
      var source = window._psShades.find(function (shade) { return shade.id === id; });
      if (!source) return;
      var duplicate = JSON.parse(JSON.stringify(source));
      duplicate.id = 'shade-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6);
      duplicate.savedAt = Date.now();
      if (duplicate.properties && duplicate.properties['Window Name']) {
        duplicate.properties['Window Name'] = (duplicate.properties['Window Name'] + ' Copy').slice(0, 40);
        duplicate.editorState = duplicate.editorState || {};
        duplicate.editorState.windowName = duplicate.properties['Window Name'];
      }
      var index = window._psShades.indexOf(source);
      window._psShades.splice(index + 1, 0, duplicate);
      saveShadesToStorage();
      renderShadeList();
      CORE.showMsg('success', 'Shade duplicated.');
    };

    window.psEditShade = function (id) {
      var shade = window._psShades.find(function (item) { return item.id === id; });
      if (!shade) return;
      if (shade.sourceProductHandle && shade.sourceProductHandle !== CFG.sourceProductHandle) {
        var ls = safeLocalStorage();
        try { if (ls) ls.setItem(PENDING_EDIT_KEY, id); } catch (e) { /* ignore */ }
        window.location.href = shade.sourceProductUrl || ('/products/' + shade.sourceProductHandle + '#shade-builder');
        return;
      }
      _editingShadeId = id;
      restoreShadeIntoForm(shade);
    };

    window.psCancelShadeEdit = function () {
      _editingShadeId = null;
      resetForm();
      setEditingUi(null);
      CORE.clearMsg();
    };

    window.psClearShades = function () {
      if (!window._psShades.length) return;
      // Light-touch confirmation — single shade lists usually don't need it,
      // but for >1 we ask before nuking the customer's work.
      if (window._psShades.length > 1 && !window.confirm('Clear all ' + window._psShades.length + ' shades from your project?')) return;
      window._psShades = [];
      _editingShadeId = null;
      saveShadesToStorage();
      setEditingUi(null);
      showListNotice(null);
      renderShadeList();
    };

    function refreshDisplayedPrices() {
      var variantIds = [];
      window._psShades.forEach(function (shade) {
        var id = String(shade.variantId || '');
        if (id && variantIds.indexOf(id) === -1) variantIds.push(id);
      });
      if (!variantIds.length) return Promise.resolve();
      return Promise.all(variantIds.map(function (variantId) {
        return fetch('/variants/' + encodeURIComponent(variantId) + '.js').then(function (response) {
          if (!response.ok) return null;
          return response.json();
        }).then(function (variant) {
          if (!variant || variant.price == null) return;
          window._psShades.forEach(function (shade) {
            if (String(shade.variantId) === variantId) shade.price = Number(variant.price) / 100;
          });
        }).catch(function () { /* cart validation will report unavailable variants later */ });
      })).then(function () {
        saveShadesToStorage();
        renderShadeList();
      });
    }

    function refreshSavedShadeData() {
      var handles = [];
      window._psShades.forEach(function (shade) {
        if (shade.sourceProductHandle && handles.indexOf(shade.sourceProductHandle) === -1) handles.push(shade.sourceProductHandle);
      });
      var variantChecks = window._psShades.map(function (shade) {
        return fetch('/variants/' + encodeURIComponent(shade.variantId) + '.js').then(function (response) {
          if (!response.ok) {
            var name = (shade.properties && shade.properties['Window Name']) || shade.label || 'A saved shade';
            throw new Error(name + ' is no longer available. Please edit it before adding your order to cart.');
          }
          return response.json();
        }).then(function (variant) {
          if (variant && variant.price != null) shade.price = Number(variant.price) / 100;
          shade.stale = false;
          delete shade.priceWas;
        });
      });
      var deliveryChecks = handles.map(function (handle) {
        return fetch('/products/' + encodeURIComponent(handle)).then(function (response) {
          if (!response.ok) return '';
          return response.text();
        }).then(function (html) {
          var deliveryWindow = '';
          var deliveryCalculatedAt = '';
          if (html && window.DOMParser) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var deliveryInput = doc.querySelector('input[name="properties[_Estimated Delivery Window]"]');
            var calculatedInput = doc.querySelector('input[name="properties[_delivery_calculated_at]"]');
            deliveryWindow = deliveryInput ? deliveryInput.value : '';
            deliveryCalculatedAt = calculatedInput ? calculatedInput.value : '';
          }
          window._psShades.forEach(function (shade) {
            if (shade.sourceProductHandle !== handle) return;
            shade.properties = shade.properties || {};
            if (deliveryWindow) shade.properties['_Estimated Delivery Window'] = deliveryWindow;
            else delete shade.properties['_Estimated Delivery Window'];
            if (deliveryCalculatedAt) shade.properties['_delivery_calculated_at'] = deliveryCalculatedAt;
            else delete shade.properties['_delivery_calculated_at'];
          });
        });
      });
      return Promise.all([Promise.all(variantChecks), Promise.all(deliveryChecks)]).then(function () {
        saveShadesToStorage();
        renderShadeList();
      });
    }

    window.psAddAllToCart = function () {
      if (!window._psShades.length || _cartRequestPending) return;
      _cartRequestPending = true;
      var btn = $('ps-add-all-btn');
      if (btn) { btn.disabled = true; btn.textContent = 'Checking your shades…'; }
      CORE.clearMsg();

      return refreshSavedShadeData().then(function () {
        var items = [];
        window._psShades.forEach(function (s) {
          items.push({
            id:         s.variantId,
            quantity:   s.quantity || 1,
            properties: s.properties || {}
          });
          var bracketConfig = s.brackets || copyBracketConfig();
          if (bracketConfig.enabled && bracketConfig.freeVariantId) {
            items.push({
              id: bracketConfig.freeVariantId,
              quantity: (s.quantity || 1) * Number(bracketConfig.freePerShade || 2),
              properties: { '_included_with_shade': (bracketConfig.freePerShade || 2) + ' per shade' }
            });
          }
          if (bracketConfig.enabled && bracketConfig.paidVariantId && s.extraBrackets > 0) {
            items.push({
              id: bracketConfig.paidVariantId,
              quantity: s.extraBrackets,
              properties: { '_extra_brackets': 'Yes' }
            });
          }
        });

        if (btn) btn.textContent = 'Adding…';
        return addCartItems(items).then(function () {
          window._psShades = [];
          _editingShadeId = null;
          saveShadesToStorage();
          window.location.href = cartRoot() + 'cart';
        });
      }).catch(function (error) {
        _cartRequestPending = false;
        if (btn) { btn.disabled = false; renderShadeList(); }
        showCartError(error);
      });
    };

    // ── Sticky bottom bar ────────────────────────────────────────────────
    var _stickyTickRequested = false;
    function setStickyVisible(bar, visible) {
      bar.hidden = !visible;
      bar.classList.toggle('ps-sticky-bar--visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      document.body.classList.toggle('ps-sticky-bar-visible', visible);
    }
    function updateStickyBar() {
      var bar = $('ps-sticky-bar');
      if (!bar) return;
      var atcWrap = $('ps-atc-wrap');
      // Update price text from current dim selection
      var amountEl = $('ps-sticky-bar-amount');
      var labelEl  = $('ps-sticky-bar-label');
      var dims = CORE.getDims();
      var price = null;
      if (dims.w > 0 && dims.h > 0) {
        var tier = CORE.getTier(dims.w, dims.h);
        if (tier && tier.price) {
          price = Number(tier.price) * (parseInt($('ps-qty') ? $('ps-qty').value : '1') || 1);
        }
      }
      if (amountEl) {
        if (price !== null) {
          amountEl.textContent = CORE.fmtMoney(price);
        } else if (CFG.initialTierPrice) {
          amountEl.textContent = CORE.fmtMoney(CFG.initialTierPrice);
        } else {
          amountEl.textContent = CFG.priceFromText || (CFG.priceDefaultText || 'Enter size');
        }
      }
      if (labelEl) {
        var bits = [];
        var qty = parseInt(($('ps-qty') || {}).value) || 1;
        if (price !== null) {
          bits.push(qty > 1 ? (qty + ' shades') : '1 shade');
        } else {
          bits.push('Enter size to see price');
        }
        if (window._psShades.length) {
          bits.push(window._psShades.length + ' in project');
        }
        labelEl.textContent = bits.join(' · ');
      }
      // Match the standard PDP helper: never cover the primary Add to Cart row.
      if (!atcWrap) { setStickyVisible(bar, true); return; }
      var rect = atcWrap.getBoundingClientRect();
      var primaryAtcIsVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      if (primaryAtcIsVisible) {
        setStickyVisible(bar, false);
      } else {
        setStickyVisible(bar, true);
      }
    }
    function scheduleStickyUpdate() {
      if (_stickyTickRequested) return;
      _stickyTickRequested = true;
      window.requestAnimationFrame(function () {
        _stickyTickRequested = false;
        updateStickyBar();
      });
    }

    function initStickyBar() {
      var bar = $('ps-sticky-bar');
      if (!bar) return;
      window.addEventListener('scroll', scheduleStickyUpdate, { passive: true });
      window.addEventListener('resize', scheduleStickyUpdate, { passive: true });
      // Update bar when dims/qty change so price is live
      ['ps-width-whole', 'ps-width-frac', 'ps-height-whole', 'ps-height-frac', 'ps-qty']
        .forEach(function (id) {
          var el = $(id);
          if (!el) return;
          el.addEventListener('change', updateStickyBar);
          el.addEventListener('input', updateStickyBar);
        });
      updateStickyBar();
    }

    // ── Init ─────────────────────────────────────────────────────────────
    function init() {
      // Day shade default summary text
      if (DAY_OPTS.length) {
        CORE.setText('ps-day-shade-summary', _selectedDayName || 'Select sheer');
      }
      // Bracket initial display
      if (BRACK.enabled) setExtraBrackets(0);
      // Load any previously-saved project shades (Option B: localStorage persistence)
      if (CFG.enableProjectBuilder) {
        var loaded = loadShadesFromStorage();
        if (loaded.shades.length) {
          window._psShades = loaded.shades;
          var restoredLbl = $('ps-shade-list-restored-label');
          if (restoredLbl) {
            restoredLbl.textContent = 'Restored from your previous session.';
          }
          if (loaded.notices.length) {
            showListNotice(loaded.notices.join(' '));
          }
        }
      }
      // Project list initial render (handles both empty + restored cases)
      renderShadeList();
      if (window._psShades.length) refreshDisplayedPrices();
      if (CFG.enableProjectBuilder) {
        var ls = safeLocalStorage();
        var pendingEditId = null;
        try {
          if (ls) {
            pendingEditId = ls.getItem(PENDING_EDIT_KEY);
            if (pendingEditId) ls.removeItem(PENDING_EDIT_KEY);
          }
        } catch (e) { /* storage may be unavailable */ }
        if (pendingEditId) {
          var pendingShade = window._psShades.find(function (shade) { return shade.id === pendingEditId; });
          if (pendingShade && (!pendingShade.sourceProductHandle || pendingShade.sourceProductHandle === CFG.sourceProductHandle)) {
            _editingShadeId = pendingEditId;
            setTimeout(function () { restoreShadeIntoForm(pendingShade); }, 0);
          }
        }
      }
      // Sticky bar
      if (CFG.enableStickyBar) initStickyBar();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  });
})();
